import CAModal from '@ds/components/containers/CAModal'
import CAInput from '@ds/components/form-elements/CAInput'
import api from '@vuejs/services/api'
import { renameContentPackageFolder } from '@vuejs/services/packages/'
import type { ContentPackageFolder } from '@vuejs/services/packages/interfaces'
import GlobalStore from '@vuejs/stores/GlobalStore'
import { defineComponent, type PropType } from 'vue'

import { stringToNumber } from '../utils'

export default defineComponent({
	name: 'RenameFolder',

	props: {
		folder: {
			required: true,
			type: Object as PropType<ContentPackageFolder>,
		},
	},

	emits: {
		cancel: () => true,
		renamed: (_id: number, _value: string) => true,
	},

	data(): Data {
		return {
			isProcessing: false,
			renameFolderName: '',
		}
	},

	computed: {
		isDisabled(): boolean {
			return !this.renameFolderName
		},

		modalName(): string {
			return 'rename-content-package-folder-modal'
		},
	},

	mounted() {
		this.renameFolderName = this.folder.name
	},

	methods: {
		closeModal() {
			GlobalStore.resetCAModal()
			this.renameFolderName = ''
			this.$emit('cancel')
		},

		async renameContentPackageFolder() {
			if (!this.renameFolderName) {
				return
			}

			const folderId = typeof this.folder.id === 'string' ? stringToNumber(this.folder.id) : this.folder.id

			this.isProcessing = true

			if (folderId) {
				try {
					await renameContentPackageFolder(api, {
						id: folderId,
						name: this.renameFolderName,
					})
					this.$emit('renamed', folderId, this.renameFolderName)
					GlobalStore.onHandleSuccessMessages({ message: `"${this.folder.name}" renamed to "${this.renameFolderName}"` })
					this.closeModal()
				} catch (error) {
					const errorMessage = error instanceof Error && error.message ? error.message : 'An error occurred'

					GlobalStore.onHandleErrorMessages({ error: errorMessage })
				} finally {
					this.isProcessing = false
				}
			}
		},
	},

	render() {
		return (
			<CAModal
				alignment="left-aligned"
				dataTest={this.modalName}
				disabled={this.isDisabled}
				isOpen={GlobalStore.emittedEvent === this.modalName}
				submitText="Rename"
				title={`Rename ${this.folder.name}`}
				onCancel={this.closeModal}
				onSubmit={this.renameContentPackageFolder}
			>
				<CAInput
					autofocus={true}
					class="mb-8"
					dataTest="rename-content-package-folder"
					id="renameFolder"
					inputRef="renameFolderId"
					label="Folder name"
					labelAppearance="static"
					required={true}
					v-model={this.renameFolderName}
					onEnter={this.renameContentPackageFolder}
				/>
			</CAModal>
		)
	},
})

interface Data {
	isProcessing: boolean
	renameFolderName: string
}
